import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';

import { useMatchesViewport } from '../../../hooks/media';
import { getEnterExitHandler } from '../../../utils/keyboard';

interface UseResizeListenerOptions {
  imageContainer: MutableRefObject<HTMLDivElement>;
  carouselContainer: MutableRefObject<HTMLDivElement>;
  closeOverlay: () => void;
}

const enum OrientationType {
  Portrait = 'PORTRAIT',
  Landscape = 'LANDSCAPE',
}

export const useResizeListener = ({
  imageContainer,
  carouselContainer,
  closeOverlay,
}: UseResizeListenerOptions) => {
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();
  const [orientation, setOrientation] = useState<OrientationType>();
  const [innerHeightViewport, setInnerHeightViewport] = useState<string>();

  useEffect(() => {
    const keyDownHandler = getEnterExitHandler(() => closeOverlay());
    const resizeHandler = () => {
      const containerHeight = Math.floor(imageContainer.current?.getBoundingClientRect().height);
      const orientationType =
        window.innerHeight > window.innerWidth
          ? OrientationType.Portrait
          : OrientationType.Landscape;

      if (!matchesDesktop && orientationType !== orientation) {
        closeOverlay();
      }

      if (carouselContainer.current) {
        carouselContainer.current.style.maxWidth = `${containerHeight}px`;
      }

      setInnerHeightViewport(`${window.innerHeight}px`);
      setOrientation(orientationType);
    };
    resizeHandler();

    window.addEventListener<'keydown'>('keydown', keyDownHandler, {
      passive: true,
    });
    window.addEventListener('resize', resizeHandler, {
      passive: true,
    });

    return () => {
      window.removeEventListener<'keydown'>('keydown', keyDownHandler);
      window.removeEventListener('resize', resizeHandler);
    };
  }, [innerHeightViewport, orientation, matchesDesktop]);

  return { innerHeightViewport };
};
